import { Alert, Box, Button, Form, FormButtonRow, FormGroup, Heading, Text, TextInput } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { getSelectedLanguage, translateKey } from '@frontend/translate';
import type { IActivationPractitioner } from './ActivateAccount';
import { NewLoginView } from '../../../../common/enums/newLoginView';
import { ValidationErrorCode } from '../../../../common/enums/validationErrorCode';
import { buildName } from '../../../../common/utils/buildName';
import { practitionerEmailOnlySchema } from '../../../../common/validation/practitioner';
import { fetchJson } from '../../../store/fetch';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import { SetPassword } from './SetPassword';

interface IResetPasswordProps {
    token: string;
}

export function ResetPassword(props: IResetPasswordProps) {
    const navigate = useNavigate();
    const translate = getSelectedLanguage();
    const [emailAddress, setEmailAddress] = useState('');
    const [view, setView] = useState<NewLoginView>(NewLoginView.createPassword);
    const [practitioner, setPractitioner] = useState<IActivationPractitioner | null>(null);
    const [validationErrors, setValidationErrors] = useState(emptyValidationErrors);
    const [resetEmailSent, setResetEmailSent] = useState(false);
    const [displayName, setDisplayName] = useState('');
    const [incorrectEmailAddress, setIncorrectEmailAddress] = useState(false);

    useEffect(() => {
        loadToken();
    }, []);

    if ([NewLoginView.expiredToken, NewLoginView.alreadyConsumedToken].includes(view)) {
        return (
            <>
                <Heading>Wachtwoord wijzigen</Heading>
                {!resetEmailSent && (
                    <>
                        <Alert
                            appearance="warning"
                            noMargin={true}
                        >
                            De link is verlopen vanwege veiligheidsoverwegingen. Vul je e-mailadres in om opnieuw een
                            link te ontvangen.
                        </Alert>
                        <Text>
                            Geen probleem. Vul je e-mailadres in, dan sturen we je een link via e-mail waarmee je je
                            wachtwoord opnieuw kunt instellen.
                        </Text>
                        <Form
                            onSubmit={async (e) => {
                                e.preventDefault();
                                await sendEmail();
                            }}
                            submitOnEnter={true}
                        >
                            <FormGroup
                                label="E-mailadres"
                                layout="vertical"
                            >
                                <TextInput
                                    error={
                                        validationErrors.get('emailAddress')
                                            ? translateKey('validation.' + validationErrors.get('emailAddress'))
                                            : undefined
                                    }
                                    name="emailAddress"
                                    onBlur={() => validateEmail()}
                                    onChange={(e) => {
                                        setEmailAddress(e.target.value);
                                        setIncorrectEmailAddress(false);
                                    }}
                                    type="text"
                                    value={emailAddress}
                                />
                            </FormGroup>
                            {incorrectEmailAddress && (
                                <Alert appearance="warning">Dit e-mailadres is niet bij ons bekend.</Alert>
                            )}
                            <FormButtonRow>
                                <Button
                                    color="secondary"
                                    type="submit"
                                    width="full"
                                >
                                    {translate.common.next}
                                </Button>
                            </FormButtonRow>
                        </Form>
                    </>
                )}
                {resetEmailSent && (
                    <>
                        <Text>
                            Een e-mail is verstuurd naar <b>{emailAddress}</b>. Klik daar op de link om je wachtwoord
                            opnieuw in te stellen.
                        </Text>
                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="column"
                        >
                            <img
                                alt="Activatie"
                                src="/assets/images/activatie.svg"
                            />
                        </Box>
                    </>
                )}
            </>
        );
    } else if (view === NewLoginView.tokenResent) {
        return (
            <>
                <Heading>Wachtwoord wijzigen</Heading>
                <Text>
                    Een e-mail is verstuurd naar <b>{emailAddress}</b>. Klik daar op de link om je wachtwoord opnieuw in
                    te stellen.
                </Text>
                <Box
                    alignItems="center"
                    display="flex"
                    flexDirection="column"
                >
                    <img
                        alt="Activatie"
                        src="/assets/images/activatie.svg"
                    />
                </Box>
            </>
        );
    } else {
        return (
            <SetPassword
                activationToken={props.token}
                practitionerDisplayName={displayName}
                redirectPath="/"
                showSuccessScreen
                title={'Wachtwoord wijzigen'}
                userName={practitioner?.userName}
            />
        );
    }

    async function loadToken() {
        const result = await fetchJson(
            'api/activationTokens/' + props.token,
            'GET',
            undefined,
            [ValidationErrorCode.ACTIVATION_TOKEN_EXPIRED, ValidationErrorCode.ACTIVATION_TOKEN_ALREADY_CONSUMED],
            [404]
        );
        if (result.errorCode) {
            return setView(NewLoginView.expiredToken);
        } else if (result.json?.practitioner) {
            setPractitioner(result.json.practitioner);
            setEmailAddress(result.json.practitioner.emailAddress);
            if (!result.json.practitioner.isActivated) {
                navigate('?view=activate&token=' + props.token);
            }
            setDisplayName(
                buildName(
                    result.json.practitioner?.nameGiven,
                    result.json.practitioner?.nameInfix,
                    result.json.practitioner?.nameFamily
                )
            );
        }
    }

    function validateEmail() {
        setValidationErrors(
            updateValidationErrorsForOne(
                practitionerEmailOnlySchema,
                { emailAddress },
                validationErrors,
                'emailAddress'
            )
        );
    }

    async function sendEmail() {
        const { hasErrors, errors } = validate(practitionerEmailOnlySchema, { emailAddress });
        setValidationErrors(errors);

        if (!hasErrors) {
            const result = await fetchJson({
                allowedHttpErrorCodes: [404],
                body: {
                    emailAddress,
                },
                method: 'POST',
                url: '/api/resetPassword',
            });

            if (result.status === 404) {
                setIncorrectEmailAddress(true);
            } else {
                setResetEmailSent(true);
            }
        }
    }
}
